/**
 * BootScene — preload assets, register procedural textures, lock landscape.
 *
 * Asset resolution: fetch the user-supplied PNG (`assets_user/<key>.png`)
 * first; if it's 404, fall back to the auto-extracted
 * `assets/<key>.png`. We bypass Phaser's loader here because the loader
 * has already finished processing by the time FILE_LOAD_ERROR fires, so
 * adding a fallback load inside that handler never executes.
 *
 * Once a PNG is fetched, we register it with the Texture Manager directly
 * via `textures.addImage(key, blobURL)` so the rest of the game can use it
 * as if it had been queued through `load.image`.
 *
 * Procedural textures (bullet, beam, chomp, etc.) are baked by
 * `registerGameTextures()` and don't need this flow.
 */
import Phaser from "phaser";
import { TEX, registerGameTextures } from "../textures";
/**
 * Keys the game loads from PNG files. Procedural textures (bullet, beam,
 * chomp, explosion, cardboard, can, boss) are baked by
 * `registerGameTextures()` instead, so they're not in this list.
 *
 * Background PNGs are intentionally NOT loaded — BackgroundSystem renders
 * them procedurally (solid black + procedural stars/planets). When the
 * user ships a `bg_<level>.png`, it goes through the same PNG loader and
 * the BackgroundSystem will pick it up as a fallback to the procedural
 * art — but for now we just don't try.
 */
const PNG_KEYS: string[] = [
  TEX.PLAYER,    // 320x190 newt sprite from user art
  TEX.PLAYER_GODZILLA, // 320x190 player godzilla sprite
  TEX.SPLASH,    // 1024x1024 menu splash from user art
  TEX.SUNGLASSES, // The sunglasses!
  // Basic enemies if provided
];

PNG_KEYS.push(
  // UI
  "tex_life",
  "tex_hitpoint",
  "trashstock_logo",
  TEX.CHOMP
);

/**
 * Fetch a PNG and register it as a Phaser texture. Tries the user path
 * first, then the auto-extracted fallback, then returns false. When a
 * texture loads, we register it via `textures.addImage(key, img)` so
 * the rest of the game can use it as if it had been queued through
 * `load.image`.
 *
 * Returns true if any candidate succeeded, false otherwise.
 */
async function fetchAndRegister(
  key: string,
  textureManager: Phaser.Textures.TextureManager
): Promise<boolean> {
  const candidates = [
    `assets/graphics/${key}.png`,
    `assets/${key}.png`
  ];
  for (const url of candidates) {
    try {
      const res = await fetch(url, { cache: "no-cache" });
      if (!res.ok) continue;
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.crossOrigin = "anonymous";
      const loaded = await new Promise<boolean>((resolve) => {
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = blobUrl;
      });
      URL.revokeObjectURL(blobUrl);
      if (!loaded) continue;
      // `addImage` accepts an HTMLImageElement directly.
      textureManager.addImage(key, img);
      return true;
    } catch {
      // ignore — try next candidate
    }
  }
  return false;
}

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: "BootScene" });
  }

  public create(): void {
    // Defer everything until the preload loop finishes — if we transition to
    // StartScene now, this scene gets destroyed mid-load and the fetch
    // promises lose their `this.textures` reference. See `preload()` below.
  }

  public preload(): void {
    // Loading overlay — pure white text on black, centered.
    const width = this.scale.width;
    const height = this.scale.height;
    const loadingText = this.add.text(width / 2, height / 2 + 20, "LOADING...", {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: "20px",
      color: "#ffffff",
      padding: { top: 20, bottom: 20, left: 20, right: 20 },
    });
    loadingText.setOrigin(0.5, 0.5);

    let loaded = 0;

    // Audio loading is disabled until real .ogg files are provided.
    // The current placeholder files are not valid audio and would crash
    // the Web Audio decoder, blocking the entire scene pipeline.
    // To re-enable: replace placeholders with real OGG files and uncomment.
    this.load.audio('bgm', 'assets/audio/bgm.ogg');
    this.load.audio('godzilla_bgm', 'assets/audio/godzilla_bgm.ogg');
    this.load.audio('boss_bgm', 'assets/audio/boss_bgm.ogg');
    this.load.audio('laser_short', 'assets/audio/laser_short.ogg');
    this.load.audio('laser_long', 'assets/audio/laser_long.ogg');
    this.load.audio('shoot', 'assets/audio/shoot.ogg');
    this.load.audio('chomp', 'assets/audio/chomp.ogg');
    this.load.audio('charge', 'assets/audio/charge.ogg');
    this.load.audio('laser', 'assets/audio/laser.ogg');
    this.load.audio('enemy_shoot', 'assets/audio/enemy_shoot.ogg');
    this.load.audio('boss_shoot', 'assets/audio/boss_shoot.ogg');
    this.load.audio('powerup', 'assets/audio/powerup.ogg');
    this.load.start();

    void (async (): Promise<void> => {
      let totalToLoad = PNG_KEYS.length;
      
      // Basic static keys
      for (const key of PNG_KEYS) {
        let ok = false;
        try {
          ok = await fetchAndRegister(key, this.textures);
        } catch (e) {
          console.error(`[BootScene] fetchAndRegister(${key}) threw:`, e);
        }
        if (ok) loaded++;
        loadingText.setText(`LOADING... ${Math.round((loaded / totalToLoad) * 100)}%`);
      }
      
      // Dynamic variants
      const KINDS = ["biowaste", "cardboard", "can", "plastic", "ewaste", "glass", "toxic", "radioactive", "tire", "battery", "rareearth", "satellite", "chemicals"];
      for (const k of KINDS) {
        for (let i = 1; i <= 40; i++) {
          const key = `enemy_${k}_${i}`;
          let ok = false;
          try {
            ok = await fetchAndRegister(key, this.textures);
          } catch (e) {
          }
          if (ok) {
            loaded++;
            // Slightly fake total update so % keeps moving if more variants found
            totalToLoad = Math.max(totalToLoad, loaded + 1);
            loadingText.setText(`LOADING... ${Math.round((loaded / totalToLoad) * 100)}%`);
          } else {
            break; // Stop checking this enemy kind once a number is missing!
          }
        }
      }

      // Done — register procedural textures, lock orientation, transition.
      loadingText.destroy();
      registerGameTextures(this);
      this.tryLockLandscape();
      this.cameras.main.setBackgroundColor("#000000");
      this.scene.start("StartScene");
    })();
  }

  /**
   * Lock the screen orientation to landscape (best-effort).
   */
  private tryLockLandscape(): void {
    try {
      const so = (screen as Screen & {
        orientation?: {
          lock?: (orientation: string) => Promise<void>;
        };
      }).orientation;
      if (so?.lock) {
        void so.lock("landscape").catch(() => undefined);
      }
    } catch {
      // ignore
    }
  }
}